import React, { memo } from 'react';
import {
  Play,
  Square,
  Repeat,
  Zap,
  Volume2,
  Swords,
  Wand2,
  TreePine,
  Users,
  Footprints,
  Bell,
  Music,
  DoorOpen,
  Ghost,
  Flame,
  AudioLines,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Slider } from '../ui/slider';

// ─────────────────────────────────────────────────────────────────────────────
// Icon mapping based on sound type
// ─────────────────────────────────────────────────────────────────────────────
const TYPE_ICONS = {
  combat: Swords,
  magic: Wand2,
  nature: TreePine,
  creature: Ghost,
  crowd: Users,
  footstep: Footprints,
  bell: Bell,
  music: Music,
  door: DoorOpen,
  explosion: Flame,
  tone: AudioLines,
  sfx: Zap,
};

function getSoundIcon(sound) {
  return TYPE_ICONS[sound.type] || Zap;
}

// ─────────────────────────────────────────────────────────────────────────────
// SoundCard — compact pad for the soundboard
// ─────────────────────────────────────────────────────────────────────────────
const SoundCard = memo(function SoundCard({
  sound,
  isActive,
  volume,
  defaultVolume,
  onToggle,
  onSetVolume,
}) {
  const TypeIcon = getSoundIcon(sound);
  const currentVolume = volume ?? defaultVolume;

  return (
    <button
      type="button"
      onClick={() => onToggle(sound.id)}
      className={cn(
        'group relative flex flex-col items-start gap-1 rounded-lg border p-2.5 text-left transition-all duration-200 w-full min-h-[72px]',
        'hover:shadow-md hover:border-primary/40',
        isActive
          ? 'border-primary/60 bg-primary/10 shadow-sm shadow-primary/20 ring-1 ring-primary/30'
          : 'border-border/60 bg-card/60 hover:bg-card',
      )}
    >
      {/* Header row: icon + loop badge */}
      <div className="flex items-start gap-1.5 w-full min-w-0">
        <TypeIcon
          className={cn(
            'h-3.5 w-3.5 shrink-0 mt-0.5',
            isActive ? 'text-primary' : 'text-muted-foreground',
          )}
        />
        <span className="text-[11px] font-medium flex-1 leading-snug break-words line-clamp-2" title={sound.displayName || sound.name}>
          {sound.displayName || sound.name}
        </span>

        {/* Loop / Oneshot indicator */}
        {sound.isLoop ? (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wider shrink-0',
              isActive
                ? 'bg-primary/20 text-primary'
                : 'bg-muted text-muted-foreground',
            )}
            title="Lecture en boucle"
          >
            <Repeat className="h-2.5 w-2.5" />
          </span>
        ) : (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wider shrink-0',
              isActive
                ? 'bg-amber-500/20 text-amber-600 dark:text-amber-400'
                : 'bg-muted text-muted-foreground',
            )}
            title="Son ponctuel"
          >
            <Zap className="h-2.5 w-2.5" />
          </span>
        )}
      </div>

      {/* Play/Stop indicator */}
      <div className="flex items-center gap-1.5 w-full">
        {isActive ? (
          <Square className="h-3 w-3 text-destructive shrink-0" />
        ) : (
          <Play className="h-3 w-3 text-muted-foreground group-hover:text-primary shrink-0" />
        )}

        {/* Volume slider - stops propagation */}
        <div
          className="flex items-center gap-1 flex-1 min-w-0"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Volume2
            className={cn(
              'h-3 w-3 shrink-0',
              isActive ? 'text-primary/70' : 'text-muted-foreground/50',
            )}
          />
          <Slider
            value={[currentVolume * 100]}
            onValueChange={([val]) => onSetVolume(sound.id, val / 100)}
            max={100}
            step={1}
            className="flex-1"
            aria-label={`Volume ${sound.name}`}
          />
          <span className="text-[9px] text-muted-foreground tabular-nums w-6 text-right shrink-0">
            {Math.round(currentVolume * 100)}
          </span>
        </div>
      </div>

      {/* Active animation indicator */}
      {isActive && (
        <div className="absolute top-1 right-1">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
          </span>
        </div>
      )}
    </button>
  );
});

export default SoundCard;
